import React, { useEffect, useRef, useState } from 'react';
import {
  PencilSquareIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
  PlusCircleIcon,
} from '@heroicons/react/24/outline';
import Layout from '../../components/Layout';
import { billingUomAPI } from '../../api/waybillAPI';

const inputCls = 'w-full border-b border-teal-400 focus:outline-none text-sm py-0.5 px-1 bg-teal-50';

export default function UnitOfMeasurePage() {
  const [uoms,      setUoms]      = useState([]);
  const [editingId, setEditingId] = useState(null); // _id | 'new' | null
  const [editForm,  setEditForm]  = useState({});
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState('');

  const codeInputRef = useRef(null);

  useEffect(() => {
    billingUomAPI.getAll()
      .then((r) => setUoms(r.data?.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (editingId === 'new' && codeInputRef.current) {
      codeInputRef.current.focus();
    }
  }, [editingId]);

  function setField(key) {
    return (e) => setEditForm((prev) => ({ ...prev, [key]: e.target.value }));
  }

  function startEdit(row) {
    setEditingId(row._id);
    setEditForm({
      UnitOfMeasureId:   row.UnitOfMeasureId,
      UnitOfMeasureName: row.UnitOfMeasureName,
      Abbreviation:      row.Abbreviation || '',
    });
    setError('');
  }

  function handleCancel() {
    setEditingId(null);
    setEditForm({});
    setError('');
  }

  function handleAddRow() {
    setEditingId('new');
    setEditForm({ UnitOfMeasureId: '', UnitOfMeasureName: '', Abbreviation: '' });
    setError('');
  }

  async function handleSave() {
    setError('');
    if (!editForm.UnitOfMeasureName?.trim()) {
      setError('Name is required.');
      return;
    }
    if (editingId === 'new' && !editForm.UnitOfMeasureId?.trim()) {
      setError('Code is required.');
      return;
    }
    setSaving(true);
    try {
      if (editingId === 'new') {
        const res = await billingUomAPI.create({
          UnitOfMeasureId:   editForm.UnitOfMeasureId.trim(),
          UnitOfMeasureName: editForm.UnitOfMeasureName.trim(),
          Abbreviation:      editForm.Abbreviation?.trim() || undefined,
        });
        if (res.status === 409) { setError('Code already exists.'); return; }
        const newDoc = res.data?.data;
        setUoms((prev) =>
          [...prev, newDoc].sort((a, b) =>
            a.UnitOfMeasureName.localeCompare(b.UnitOfMeasureName)
          )
        );
      } else {
        const res = await billingUomAPI.update(editingId, {
          UnitOfMeasureName: editForm.UnitOfMeasureName.trim(),
          Abbreviation:      editForm.Abbreviation?.trim() || undefined,
        });
        const updated = res.data?.data;
        setUoms((prev) => prev.map((u) => (u._id === editingId ? updated : u)));
      }
      setEditingId(null);
      setEditForm({});
    } catch (e) {
      if (e?.response?.status === 409) {
        setError('Code already exists.');
      } else {
        setError('Save failed. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this unit of measure?')) return;
    const res = await billingUomAPI.delete(id);
    if (res.status === 200 || res.data?.success) {
      setUoms((prev) => prev.filter((u) => u._id !== id));
    }
  }

  function renderReadOnlyRow(row) {
    return (
      <tr key={row._id} className="hover:bg-gray-50 border-b border-gray-100">
        <td className="px-3 py-2 text-sm text-gray-800">{row.UnitOfMeasureId}</td>
        <td className="px-3 py-2 text-sm text-gray-800">{row.UnitOfMeasureName}</td>
        <td className="px-3 py-2 text-sm text-gray-600">{row.Abbreviation || '—'}</td>
        <td className="px-3 py-2 text-center">
          <div className="flex items-center justify-center gap-1">
            <button onClick={() => startEdit(row)} disabled={editingId !== null} className="disabled:opacity-30" title="Edit">
              <PencilSquareIcon className="h-4 w-4 text-gray-400 hover:text-teal-600" />
            </button>
            <button onClick={() => handleDelete(row._id)} disabled={editingId !== null} className="disabled:opacity-30" title="Delete">
              <TrashIcon className="h-4 w-4 text-gray-400 hover:text-red-500" />
            </button>
          </div>
        </td>
      </tr>
    );
  }

  function renderEditRow(key) {
    return (
      <tr key={key} className="bg-teal-50 ring-1 ring-teal-200 ring-inset">
        <td className="px-2 py-1">
          {editingId === 'new' ? (
            <input ref={codeInputRef} type="text" value={editForm.UnitOfMeasureId}
              onChange={setField('UnitOfMeasureId')} placeholder="e.g. KG"
              className={inputCls} />
          ) : (
            <span className="text-sm text-gray-400 px-1">{editForm.UnitOfMeasureId}</span>
          )}
        </td>
        <td className="px-2 py-1">
          <input type="text" value={editForm.UnitOfMeasureName}
            onChange={setField('UnitOfMeasureName')} placeholder="Name"
            className={inputCls} required />
        </td>
        <td className="px-2 py-1">
          <input type="text" value={editForm.Abbreviation}
            onChange={setField('Abbreviation')} placeholder="e.g. kg, pcs, cbm"
            className={inputCls} />
        </td>
        <td className="px-2 py-1 text-center">
          <div className="flex items-center justify-center gap-1">
            <button onClick={handleSave} disabled={saving} title="Save">
              <CheckIcon className="h-4 w-4 text-teal-600 hover:text-teal-800" />
            </button>
            <button onClick={handleCancel} title="Cancel">
              <XMarkIcon className="h-4 w-4 text-gray-400 hover:text-gray-600" />
            </button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <Layout pageTitle="Unit of Measure">
      <div className="max-w-2xl">
        {/* Header row */}
        <div className="flex items-center justify-between mb-3">
          <h1 className="text-base font-semibold text-gray-700">Unit of Measure</h1>
          <button
            onClick={handleAddRow}
            disabled={editingId !== null}
            className="flex items-center gap-1.5 text-sm bg-teal-600 text-white px-3 py-1.5 rounded-md hover:bg-teal-700 disabled:opacity-40"
          >
            <PlusCircleIcon className="h-4 w-4" />
            Add UOM
          </button>
        </div>

        {error && <p className="text-sm text-red-600 mb-2">{error}</p>}

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full text-sm border-collapse">
            <colgroup>
              <col style={{ width: 130 }} />
              <col />
              <col style={{ width: 120 }} />
              <col style={{ width: 80 }} />
            </colgroup>
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Abbreviation</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {uoms.length === 0 && editingId !== 'new' && (
                <tr>
                  <td colSpan={4} className="text-center text-sm text-gray-400 py-6">
                    No units of measure yet. Click "Add UOM" to create one.
                  </td>
                </tr>
              )}
              {uoms.map((row) =>
                editingId === row._id ? renderEditRow(row._id) : renderReadOnlyRow(row)
              )}
              {editingId === 'new' && renderEditRow('new')}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
